import "./SignupForm.css";
import { useStore } from "@nanostores/react";
import { Button as VideoScribeButton } from "@sparkol/videoscribe-react-ui";

import ReCAPTCHA from "react-google-recaptcha";
import React, { useEffect, useRef, useState } from "react";
import Form from "./Form";
import FormHeader from "./FormHeader";
import useAuthErrors, { type ServerError } from "../hooks/useAuthErrors";
import { isBrowser } from "../../../lib/ssr";
import { trackButtonPress, trackMixpanelEvent } from "../../../lib/mixpanel";
import Button from "../Button/Button";
import GoogleLogo from "./GoogleLogo.tsx";
import Errors from "./Errors";
import Divider from "./Divider";
import { setPostLoginRedirect, setSignupLocation } from "../../../lib/storage";
import { signInWithRedirect, signUp } from "../../../lib/auth";
import {
  trackGoogleSignupWithTimeout,
  trackRegisterEmail,
} from "../../../lib/ga";
import Input from "./Input";
import PasswordInput from "./PasswordInput";
import PasswordChecklist from "./PasswordChecklist";
import Checkbox from "./Checkbox";
import Spinner from "../Spinner/Spinner";
import { getDeviceType } from "../../../lib/device.ts";
import { getUTMSearchParamString } from "../../../lib/url.ts";
import { currentUser } from "../../../store.ts";

interface SignupFormProps {
  showHeader: boolean;
  title?: string;
  successRedirect?: string;
  productCode?: string;
  introText?: string;
  variant?: "light-button" | "dark-button";
}

const STORAGE_KEY = "signup-form";
const defaults = {
  email: "",
  firstName: "",
  lastName: "",
  agreeTermsConditions: false,
  marketingOptIn: false,
};

const getInitialState = () => {
  if (!isBrowser()) return defaults;

  try {
    const cookieData = window.sessionStorage.getItem(STORAGE_KEY);
    if (!cookieData) return defaults;

    const parsedData = JSON.parse(cookieData);

    if (!parsedData) return defaults;

    return { ...defaults, ...parsedData };
  } catch (err) {
    console.error(err);
    return defaults;
  }
};

const saveFormState = (field: string, value: string | number | boolean) => {
  if (!isBrowser()) return;
  try {
    const localData = window.sessionStorage.getItem(STORAGE_KEY);

    const parsedData = localData ? JSON.parse(localData) : {};

    parsedData[field] = value;

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsedData));
  } catch (err) {
    console.error(err);
  }
};

const clearStoreFormState = () => {
  if (!isBrowser()) return;
  window.sessionStorage.removeItem(STORAGE_KEY);
};

const SignupForm = ({
  showHeader,
  title,
  successRedirect = "/app/",
  productCode = "",
  introText,
  variant = "dark-button",
}: SignupFormProps) => {
  const errorsRef = useRef<HTMLDivElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const user = useStore(currentUser);

  const initialFormState = getInitialState();
  const [email, setEmail] = useState(initialFormState.email);
  const [firstName, setFirstName] = useState(initialFormState.firstName);
  const [lastName, setLastName] = useState(initialFormState.lastName);
  const [password, setPassword] = useState("");
  const [agreeTermsConditions, setAgreeTermsConditions] = useState(
    initialFormState.agreeTermsConditions,
  );
  const [marketingOptIn, setMarketingOptIn] = useState(
    initialFormState.marketingOptIn,
  );
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const {
    ACTIONS,
    ERRORS,
    errors,
    dispatch,
    getErrorsByInput,
    parseAndDispatchServerError,
  } = useAuthErrors();

  useEffect(() => {
    if (user) {
      window.location.href = successRedirect;
    }
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (errors.length && errorsRef.current) {
      const targetTop = 160;
      const { top } = errorsRef.current.getBoundingClientRect();
      const offset = top - targetTop;
      window.scrollBy({ top: offset, behavior: "smooth" });
    }
  }, [errors]);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFirstName(value);
    saveFormState("firstName", value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastName(e.target.value);
    saveFormState("lastName", value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(e.target.value);
    saveFormState("email", value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleMarketingOptIn = () => {
    const checked = !marketingOptIn;
    setMarketingOptIn(checked);
    saveFormState("marketingOptIn", checked);
  };

  const handleAgreeTermsConditions = () => {
    const checked = !agreeTermsConditions;
    setAgreeTermsConditions(checked);
    saveFormState("agreeTermsConditions", checked);
  };

  const validateRequiredFields = () => {
    const emailAction = email ? ACTIONS.REMOVE_ERROR : ACTIONS.ADD_ERROR;
    dispatch({ type: emailAction, payload: ERRORS.EMAIL_REQUIRED_ERROR });

    const firstNameAction = firstName
      ? ACTIONS.REMOVE_ERROR
      : ACTIONS.ADD_ERROR;
    dispatch({
      type: firstNameAction,
      payload: ERRORS.FIRST_NAME_REQUIRED_ERROR,
    });

    const lastNameAction = lastName ? ACTIONS.REMOVE_ERROR : ACTIONS.ADD_ERROR;
    dispatch({
      type: lastNameAction,
      payload: ERRORS.LAST_NAME_REQUIRED_ERROR,
    });

    const passwordActionType = password
      ? ACTIONS.REMOVE_ERROR
      : ACTIONS.ADD_ERROR;
    let passwordAction = {
      type: passwordActionType,
      payload: ERRORS.PASSWORD_REQUIRED_ERROR,
    };
    if (password) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
      if (!passwordRegex.test(password)) {
        passwordAction = {
          type: ACTIONS.ADD_ERROR,
          payload: ERRORS.PASSWORD_INVALID_ERROR,
        };
      }
    }
    dispatch(passwordAction);

    if (!email || !firstName || !lastName || !password) {
      return false;
    }

    return true;
  };

  const validateTermsAgreement = () => {
    const action = agreeTermsConditions
      ? ACTIONS.REMOVE_ERROR
      : ACTIONS.ADD_ERROR;
    dispatch({ type: action, payload: ERRORS.TERMS_AGREEMENT_ERROR });

    return agreeTermsConditions;
  };

  const handleSignupWithGoogle = async () => {
    await trackGoogleSignupWithTimeout();
    setSignupLocation(window.location.pathname);
    trackButtonPress("Web Click Create Your Free Account With Google");
    setPostLoginRedirect(successRedirect);

    try {
      await signInWithRedirect({
        provider: "Google",
      });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "UserAlreadyAuthenticatedException") {
          window.location.href = successRedirect;
          return;
        }

        parseAndDispatchServerError(error);
        throw error;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    recaptchaRef.current?.reset();

    if (loading) return;

    setLoading(true);

    dispatch({ type: ACTIONS.CLEAR_ERRORS });

    const termsConditionsValidated = validateTermsAgreement();
    const requiredFieldsValidated = validateRequiredFields();

    if (!termsConditionsValidated || !requiredFieldsValidated) {
      setLoading(false);
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.RECAPTCHA_ERROR });
      setLoading(false);
      return;
    }

    try {
      setSignupLocation(window.location.pathname);

      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
          },
          validationData: {
            recaptchaToken,
          },
          clientMetadata: {
            marketingOptIn: marketingOptIn.toString(),
            source: "videoscribe",
            productCode,
            deviceType: getDeviceType(),
          },
        },
      });

      clearStoreFormState();
      await Promise.race([
        trackRegisterEmail(),
        new Promise((resolve) => setTimeout(resolve, 5000)),
      ]);

      const utmForwardingParams = getUTMSearchParamString();
      const to = `/sign-up/please-confirm/?email=${encodeURIComponent(email)}${utmForwardingParams ? `&${utmForwardingParams}` : ""}`;
      window.location.href = to;
    } catch (error) {
      let serverError = error as ServerError;
      console.error(serverError);
      trackMixpanelEvent("Web Registration Failed", {
        "Failure Reason": serverError.message,
      });
      parseAndDispatchServerError(serverError);
      setLoading(false);
    }
  };

  const hubspotMarketingOptOutValue = marketingOptIn ? "false" : "true";
  const emailErrors = getErrorsByInput(errors, "email");
  const augmentedEmailErrors = emailErrors.map((error) => {
    if (error.id !== "USER_EXISTS_ERROR") return error;

    return {
      ...error,
      message: (
        <>
          <span>{error.message} </span>
          <span>Please </span>
          <a className="hyperlink" href="/login/">
            login here
          </a>
        </>
      ),
    };
  });

  return (
    <Form
      onSubmit={handleSubmit}
      id="sign-up-form"
      variant={variant === "dark-button" ? "borderless" : "bordered"}
    >
      {showHeader && !!title && (
        <FormHeader arrow>
          <h2>{title}</h2>
        </FormHeader>
      )}
      {introText && <p>{introText}</p>}
      {errors && errors.length > 0 && (
        <Errors ref={errorsRef} errors={errors} />
      )}

      <div>
        <Button
          dark
          fullWidth
          social
          type="button"
          onClick={handleSignupWithGoogle}
          Icon={GoogleLogo}
        >
          Sign up with Google
        </Button>
      </div>

      <Divider>Or</Divider>

      <div className="SignupForm__nameFields">
        <Input
          id="signup-first-name"
          label="First name"
          type="text"
          value={firstName}
          onChange={handleFirstNameChange}
          required
          errors={getErrorsByInput(errors, "first_name")}
          autoComplete="given-name"
        />
        <Input
          id="signup-last-name"
          label="Last name"
          type="text"
          value={lastName}
          onChange={handleLastNameChange}
          required
          errors={getErrorsByInput(errors, "last_name")}
          autoComplete="family-name"
        />
      </div>

      <Input
        id="signup-email-address"
        label="Email address"
        type="email"
        value={email}
        onChange={handleEmailChange}
        required
        errors={augmentedEmailErrors}
        autoComplete="email"
      />

      <PasswordInput
        id="signup-password"
        label="Password"
        value={password}
        onChange={handlePasswordChange}
        required
        errors={getErrorsByInput(errors, "password")}
        autoComplete="new-password"
      />

      <PasswordChecklist password={password} />

      <label className="SignupForm_hiddenField">
        marketing_email_opt_status
        <input
          type="text"
          name="marketing_email_opt_status"
          value={hubspotMarketingOptOutValue}
          readOnly
        />
      </label>

      <div>
        <Checkbox
          id="signup-terms-conditions"
          label={
            <>
              I agree to Sparkol&#39;s{" "}
              <a
                href="https://www.sparkol.com/website-terms"
                target="_blank"
                rel="noreferrer"
              >
                Website Terms of Use
              </a>{" "}
              and{" "}
              <a
                href="https://www.sparkol.com/sparkol-terms"
                target="_blank"
                rel="noreferrer"
              >
                App Terms and Conditions.
              </a>
            </>
          }
          onChange={handleAgreeTermsConditions}
          checked={agreeTermsConditions}
          errors={getErrorsByInput(errors, "terms-conditions")}
          required
        />
        <Checkbox
          id="signup-email-optin"
          label="Yes, please send me emails including product updates, news and offers."
          onChange={handleMarketingOptIn}
          checked={marketingOptIn}
        />
      </div>

      {mounted && (
        <ReCAPTCHA
          sitekey={import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY}
          size="invisible"
          ref={recaptchaRef}
        />
      )}

      <VideoScribeButton
        fullWidth
        type="submit"
        variant={variant === "dark-button" ? "secondary" : "primary"}
        onClick={() => {
          trackButtonPress("Web Click Create Your Free Account");
        }}
      >
        {loading ? <Spinner /> : "Create your FREE account"}
      </VideoScribeButton>
    </Form>
  );
};
export default SignupForm;
